import { call, put, takeLatest, select } from 'redux-saga/effects';
import * as types from './CapacityTableActionType';
import * as actions from './CapacityTableActions';
import * as services from './CapacityTableServices';
import SweetAlert from '../../../components/sweetalert/SweetAlert';


// Fetch Capacity Table Reports Saga
function* fetchCapacityTableReportsSaga(action) {
    try {
        const response = yield call(services.fetchCapacityTableReportsService, action.payload);

        if (response && response.success) {
            yield put(actions.fetchCapacityTableReportsSuccess(response));
        } else {
            yield put(actions.fetchCapacityTableReportsFailure(response?.errorMessage || 'Failed to fetch capacity table reports'));
        }
    } catch (error) {
        yield put(actions.fetchCapacityTableReportsFailure(error.message || 'An error occurred'));
    }
}

// Fetch Capacity Notes Edit Saga
function* fetchCapacityNotesEditSaga(action) {
    try {
        const response = yield call(services.fetchCapacityNotesEditService, action.payload);

        if (response && response.success) {
            yield put(actions.fetchCapacityNotesEditSuccess(response.values));
        } else {
            yield put(actions.fetchCapacityNotesEditFailure(response?.errorMessage || 'Failed to fetch notes'));
        }
    } catch (error) {
        yield put(actions.fetchCapacityNotesEditFailure(error.message || 'An error occurred'));
    }
}

// Fetch Capacity Notes Save Saga
function* fetchCapacityNotesSaveSaga(action) {
    try {
        const response = yield call(services.fetchCapacityNotesSaveService, action.payload);

        if (response && response.success) {
            yield put(actions.fetchCapacityNotesSaveSuccess(response));

            // Show success message
            SweetAlert.success({
                title: 'Success!',
                text: 'Notes saved successfully.',
                timer: 2000,
                showConfirmButton: true
            });
        } else {
            yield put(actions.fetchCapacityNotesSaveFailure(response?.errorMessage || 'Failed to save notes'));

            // Show error message
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to save notes.',
                showConfirmButton: true
            });
        }
    } catch (error) {
        yield put(actions.fetchCapacityNotesSaveFailure(error.message || 'An error occurred'));

        // Show error message
        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'An error occurred while saving notes.',
            showConfirmButton: true
        });
    }
}

// Delete Capacity Report Saga
function* deleteCapacityReportSaga(action) {
    try {
        const response = yield call(services.deleteCapacityReportService, action.payload);

        if (response && response.success) {
            SweetAlert.success({
                title: 'Deleted!',
                text: 'Capacity Table report has been deleted successfully.',
            });

            yield put(actions.deleteCapacityReportSuccess(response));

            // Refresh the reports table
            const refreshPayload = {
                SkipNoOfRecords: 0,
                PageSize: 100,
                sSearch: '',
                sortColumn: 'ReportName',
                sortColumnDir: 'asc'
            };
            yield put(actions.fetchCapacityTableReportsRequest(refreshPayload));

            // Execute onSuccess callback if provided
            if (action.meta?.onSuccess) {
                action.meta.onSuccess();
            }
        } else {
            yield put(actions.deleteCapacityReportFailure(response?.errorMessage || 'Failed to delete capacity report'));

            // Show error message
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to delete capacity report.',
                showConfirmButton: true
            });
        }
    } catch (error) {
        yield put(actions.deleteCapacityReportFailure(error.message || 'An error occurred'));

        // Show error message
        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'An error occurred while deleting the report.',
            showConfirmButton: true
        });
    }
}

// Enable/Disable Capacity Table Report Saga
function* enableDisableCapacityTableReportSaga(action) {
    try {
        const response = yield call(services.enableDisableCapacityTableReportService, action.payload);

        if (response && response.success) {
            yield put(actions.enableDisableCapacityTableReportSuccess(response));

            // Call CoverageGapTool data refresh after successful toggle
            yield put(actions.coverageGapToolDataRefreshRequest());

            // Show success message only if suppressAlert is not true
            if (!action.payload.suppressAlert) {
                SweetAlert.success({
                    title: 'Success!',
                    text: response.resultMessage || 'Report status updated successfully.',
                    timer: 2000,
                    showConfirmButton: true
                });
            }

            // Refresh the reports list to get updated data
            yield put(actions.fetchCapacityTableReportsRequest({
                SkipNoOfRecords: 0,
                PageSize: 100,
                sSearch: '',
                sortColumn: 'CapacityTableID',
                sortColumnDir: 'desc'
            }));
        } else {
            yield put(actions.enableDisableCapacityTableReportFailure(response?.errorMessage || 'Failed to update report status'));

            // Show error message
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to update report status.',
                showConfirmButton: true
            });
        }
    } catch (error) {
        yield put(actions.enableDisableCapacityTableReportFailure(error.message || 'An error occurred'));

        // Show error message
        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'An error occurred while updating the report status.',
            showConfirmButton: true
        });
    }
}

// CoverageGapTool Data Refresh Saga
function* coverageGapToolDataRefreshSaga(action) {
    try {
        const response = yield call(services.coverageGapToolDataRefreshService);

        if (response && response.success) {
            yield put(actions.coverageGapToolDataRefreshSuccess(response));
        } else {
            yield put(actions.coverageGapToolDataRefreshFailure(response?.errorMessage || 'Failed to refresh coverage gap tool data'));
        }
    } catch (error) {
        yield put(actions.coverageGapToolDataRefreshFailure(error.message || 'An error occurred'));
    }
}

// Get Rad Shift Paste Input All Dropdown List Saga
function* getRadShiftPasteInputAllDDListSaga(action) {
    try {
        const response = yield call(services.getRadShiftPasteInputAllDDListService, action.payload);

        if (response && response.success) {
            yield put(actions.getRadShiftPasteInputAllDDListSuccess(response));
        } else {
            yield put(actions.getRadShiftPasteInputAllDDListFailure(response?.errorMessage || 'Failed to fetch dropdown lists'));
        }
    } catch (error) {
        yield put(actions.getRadShiftPasteInputAllDDListFailure(error.message || 'An error occurred'));
    }
}

// Get Shift Paste Input Filter Data With Pagination Saga
function* getShiftPasteInputFilterDataWithPaginationSaga(action) {
    try {
        const response = yield call(services.getShiftPasteInputFilterDataWithPaginationService, action.payload);

        if (response && response.success) {
            yield put(actions.getShiftPasteInputFilterDataWithPaginationSuccess(response));
        } else {
            yield put(actions.getShiftPasteInputFilterDataWithPaginationFailure(response?.errorMessage || 'Failed to fetch shift paste input data'));
        }
    } catch (error) {
        yield put(actions.getShiftPasteInputFilterDataWithPaginationFailure(error.message || 'An error occurred'));
    }
}

// Get Shift Paste Input Refresh Data With Pagination Saga
function* getShiftPasteInputRefreshDataWithPaginationSaga(action) {
    try {
        const response = yield call(services.getShiftPasteInputRefreshDataWithPaginationService, action.payload);

        if (response && response.success) {
            yield put(actions.getShiftPasteInputRefreshDataWithPaginationSuccess(response));
        } else {
            yield put(actions.getShiftPasteInputRefreshDataWithPaginationFailure(response?.errorMessage || 'Failed to refresh shift paste input data'));
        }
    } catch (error) {
        yield put(actions.getShiftPasteInputRefreshDataWithPaginationFailure(error.message || 'An error occurred'));
    }
}

// Publish Capacity Table Report Saga
function* publishCapacityTableReportSaga(action) {
    try {
        const response = yield call(services.publishCapacityTableReportService, action.payload);

        if (response && response.success) {
            yield put(actions.publishCapacityTableReportSuccess(response));
            // Show success message
            SweetAlert.success({
                title: 'Success!',
                text: 'Your report has been published.',
                showConfirmButton: true
            });
        } else {
            yield put(actions.publishCapacityTableReportFailure(response?.errorMessage || 'Failed to publish capacity table report'));
            // Show error message
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to publish capacity table report',
                showConfirmButton: true
            });
        }
    } catch (error) {
        yield put(actions.publishCapacityTableReportFailure(error.message || 'An error occurred'));
        // Show error message
        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'An error occurred while publishing capacity table report',
            showConfirmButton: true
        });
    }
}

// Get All Capacity Table Published Report List Saga
function* getAllCapacityTablePublishedReportListSaga(action) {
    try {
        const response = yield call(services.getAllCapacityTablePublishedReportListService);

        if (response && response.success) {
            yield put(actions.getAllCapacityTablePublishedReportListSuccess(response));
        } else {
            yield put(actions.getAllCapacityTablePublishedReportListFailure(response?.errorMessage || 'Failed to fetch published report list'));
        }
    } catch (error) {
        yield put(actions.getAllCapacityTablePublishedReportListFailure(error.message || 'An error occurred'));
    }
}

// Get Capacity Table Report Saga
function* getCapacityTableReportSaga(action) {
    try {
        const response = yield call(services.getCapacityTableReportService, action.payload);

        if (response && response.success) {
            yield put(actions.getCapacityTableReportSuccess(response));
        } else {
            yield put(actions.getCapacityTableReportFailure(response?.errorMessage || 'Failed to fetch capacity table report'));
        }
    } catch (error) {
        yield put(actions.getCapacityTableReportFailure(error.message || 'An error occurred'));
    }
}

// Get Active Radiologist Data By ID Saga
function* getActiveRadDataByIdSaga(action) {
    try {
        const response = yield call(services.getActiveRadDataByIdService, action.payload);

        if (response && response.success) {
            yield put(actions.getActiveRadDataByIdSuccess(response));
        } else {
            yield put(actions.getActiveRadDataByIdFailure(response?.errorMessage || 'Failed to fetch active radiologist data'));
        }
    } catch (error) {
        yield put(actions.getActiveRadDataByIdFailure(error.message || 'An error occurred'));
    }
}

// Get Pending Radiologist Data By ID Saga
function* getPendingRadDataByIdSaga(action) {
    try {
        const response = yield call(services.getPendingRadDataByIdService, action.payload);

        if (response && response.success) {
            yield put(actions.getPendingRadDataByIdSuccess(response));
        } else {
            yield put(actions.getPendingRadDataByIdFailure(response?.errorMessage || 'Failed to fetch pending radiologist data'));
        }
    } catch (error) {
        yield put(actions.getPendingRadDataByIdFailure(error.message || 'An error occurred'));
    }
}

// Get Offboarding Radiologist Data By ID Saga
function* getOffboardingRadDataByIdSaga(action) {
    try {
        const response = yield call(services.getOffboardingRadDataByIdService, action.payload);

        if (response && response.success) {
            yield put(actions.getOffboardingRadDataByIdSuccess(response));
        } else {
            yield put(actions.getOffboardingRadDataByIdFailure(response?.errorMessage || 'Failed to fetch offboarding radiologist data'));
        }
    } catch (error) {
        yield put(actions.getOffboardingRadDataByIdFailure(error.message || 'An error occurred'));
    }
}

// Save Is Excluded Saga
function* saveIsExcludedSaga(action) {
    try {
        const response = yield call(services.saveIsExcludedService, action.payload);

        if (response && response.success) {
            // Show success SweetAlert
            SweetAlert.success({
                title: response.resultMessage.includes('Unlinked') ? 'Unlinked!' : 'Linked!',
                text: response.resultMessage,
                timer: 2000,
                showConfirmButton: true
            });

            yield put(actions.saveIsExcludedSuccess(response));
        } else {
            yield put(actions.saveIsExcludedFailure(response?.errorMessage || 'Failed to save IsExcluded status'));
        }
    } catch (error) {
        yield put(actions.saveIsExcludedFailure(error.message || 'An error occurred'));
    }
}

// Get Duplicate Schedule Data Saga
function* getDuplicateScheduleDataSaga(action) {
    try {
        const response = yield call(services.getDuplicateScheduleDataService, action.payload);

        if (response && response.success) {
            yield put(actions.getDuplicateScheduleDataSuccess(response));
        } else {
            yield put(actions.getDuplicateScheduleDataFailure(response?.errorMessage || 'Failed to get duplicate schedule data'));
        }
    } catch (error) {
        yield put(actions.getDuplicateScheduleDataFailure(error.message || 'An error occurred'));
    }
}

// Delete Duplicate Shift Paste Input Data Saga
function* deleteDuplicateShiftPasteInputDataSaga(action) {
    try {
        const response = yield call(services.deleteDuplicateShiftPasteInputDataService, action.payload);

        if (response && response.success) {
            yield put(actions.deleteDuplicateShiftPasteInputDataSuccess(response));

            // After successful delete, refresh the grid
            const state = yield select(state => state.capacityTableReducer);
            const capacityTableId = state.capacityTableReportData?.CapacityTableID || state.selectedCapacityTable?.CapacityTableID || 0;

            // Show success alert
            SweetAlert.success({
                title: 'Deleted!',
                text: response.resultMessage || 'Duplicate shift input data deleted successfully.',
                timer: 2000,
                showConfirmButton: true
            });
                // Also refresh duplicate list view if present
                const duplicatePayload = {
                    CapacityTableID: capacityTableId,
                    SkipNoOfRecords: 0,
                    PageSize: 1000,
                    sSearch: '',
                    sortColumn: '',
                    sortColumnDir: ''
                };

                yield put(actions.getDuplicateScheduleDataRequest(duplicatePayload));
        } else {
            yield put(actions.deleteDuplicateShiftPasteInputDataFailure(response?.errorMessage || 'Failed to delete duplicate data'));

            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to delete duplicate data',
                showConfirmButton: true
            });
        }
    } catch (error) {
        yield put(actions.deleteDuplicateShiftPasteInputDataFailure(error.message || 'An error occurred'));

        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'An error occurred while deleting duplicate data',
            showConfirmButton: true
        });
    }
}

// Save Radiologist Shift Input Details Saga
function* saveRadiologistShiftInputDetailsSaga(action) {
    try {
        const response = yield call(services.saveRadiologistShiftInputDetailsService, action.payload);

        if (response && response.success) {
            yield put(actions.saveRadiologistShiftInputDetailsSuccess(response));

            // Mark that new schedules were added so Simulator can show refresh message
            yield put(actions.updateCapacityTableReducer({ simulatorNewSchedulesAdded: true }));

            // Refresh the datatable/grid after save
            const state = yield select(state => state.capacityTableReducer);
            const capacityTableId = state.capacityTableReportData?.CapacityTableID || state.selectedCapacityTable?.CapacityTableID || 0;
            const refreshPayload = {
                CapacityTableID: capacityTableId,
                SkipNoOfRecords: 0,
                PageSize: 100,
                sSearch: '',
                sortColumn: 'IsExcluded',
                sortColumnDir: 'desc',
                IsQgendaSchedule: false,
            };
            yield put(actions.getShiftPasteInputRefreshDataWithPaginationRequest(refreshPayload));

            // Show success message only if not handled by component
            // if (!action.payload?.handleInComponent) {
                SweetAlert.success({
                    title: 'Success!',
                    text: response.resultMessage || 'Successfully saved Provider schedule.',
                    timer: 2000,
                    showConfirmButton: true
                });
            //}
        } else {
            const errorMessage = response?.errorMessage || 'Failed to save Provider schedule';
            yield put(actions.saveRadiologistShiftInputDetailsFailure(errorMessage));

            // Show error message only if not handled by component
            if (!action.payload?.handleInComponent) {
                SweetAlert.error({
                    title: 'Error!',
                    text: errorMessage,
                    showConfirmButton: true
                });
            }
        }
    } catch (error) {
        const errorMessage = error.message || 'An error occurred';
        yield put(actions.saveRadiologistShiftInputDetailsFailure(errorMessage));

        // Show error message only if not handled by component
        if (!action.payload?.handleInComponent) {
            SweetAlert.error({
                title: 'Error!',
                text: error.message || 'An error occurred while saving Provider schedule.',
                showConfirmButton: true
            });
        }
    }
} 

// Delete Shift Input Details Saga
function* deleteShiftInputDetailsSaga(action) {
    try {
        const response = yield call(services.deleteShiftInputDetailsService, action.payload);

        if (response && response.success) {
            yield put(actions.deleteShiftInputDetailsSuccess(response));

            // Show success message
            SweetAlert.success({
                title: 'Deleted!',
                text: response.resultMessage || 'Successfully deleted shift input details.',
                timer: 2000,
                showConfirmButton: true
            });

            // Refresh the shift paste input data after successful deletion
            if (action.meta?.onSuccess) {
                action.meta.onSuccess();
            }
        } else {
            yield put(actions.deleteShiftInputDetailsFailure(response?.errorMessage || 'Failed to delete shift input details'));

            // Show error message
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to delete shift input details.',
                showConfirmButton: true
            });
        }
    } catch (error) {
        yield put(actions.deleteShiftInputDetailsFailure(error.message || 'An error occurred'));

        // Show error message
        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'An error occurred while deleting shift input details.',
            showConfirmButton: true
        });
    }
}

// Get Shift Input Details By ID Saga
function* getShiftInputDetailsByIdSaga(action) {
    try {
        const response = yield call(services.getShiftInputDetailsByIdService, action.payload);

        if (response && response.success) {
            yield put(actions.getShiftInputDetailsByIdSuccess(response));
        } else {
            yield put(actions.getShiftInputDetailsByIdFailure(response?.errorMessage || 'Failed to get shift input details'));

            // Show error message
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to get shift input details.',
                showConfirmButton: true
            });
        }
    } catch (error) {
        yield put(actions.getShiftInputDetailsByIdFailure(error.message || 'An error occurred'));

        // Show error message
        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'An error occurred while getting shift input details.',
            showConfirmButton: true
        });
    }
}

// Update Radiologist Shift Input Details Saga
function* updateRadiologistShiftInputDetailsSaga(action) {
    try {
        const response = yield call(services.updateRadiologistShiftInputDetailsService, action.payload);

        if (response && response.success) {
            yield put(actions.updateRadiologistShiftInputDetailsSuccess(response));

            // Show success message only if not handled by component
            // if (!action.payload?.handleInComponent) {
                SweetAlert.success({
                    title: 'Updated!',
                    text: response.resultMessage || 'Successfully Updated Radiologist Shift Input Details.',
                    timer: 2000,
                    showConfirmButton: true
                });
            // }

            // Execute onSuccess callback if provided
            if (action.meta?.onSuccess) {
                action.meta.onSuccess();
            }
        } else {
            yield put(actions.updateRadiologistShiftInputDetailsFailure(response?.errorMessage || 'Failed to update radiologist shift input details'));

            // Show error message only if not handled by component
            if (!action.payload?.handleInComponent) {
                SweetAlert.error({
                    title: 'Error!',
                    text: response?.errorMessage || 'Failed to update radiologist shift input details.',
                    showConfirmButton: true
                });
            }
        }
    } catch (error) {
        yield put(actions.updateRadiologistShiftInputDetailsFailure(error.message || 'An error occurred'));

        // Show error message only if not handled by component
        if (!action.payload?.handleInComponent) {
            SweetAlert.error({
                title: 'Error!',
                text: error.message || 'An error occurred while updating radiologist shift input details.',
                showConfirmButton: true
            });
        }
    }
}

// Get Simulator RPCE With Pagination Saga
function* getSimulatorRPCEWithPaginationSaga(action) {
    try {
        const response = yield call(services.getSimulatorRPCEWithPaginationService, action.payload);

        if (response && response.success) {
            yield put(actions.getSimulatorRPCEWithPaginationSuccess(response));
        } else {
            yield put(actions.getSimulatorRPCEWithPaginationFailure(response?.errorMessage || 'Failed to fetch simulator RPCE data'));
        }
    } catch (error) {
        yield put(actions.getSimulatorRPCEWithPaginationFailure(error.message || 'An error occurred'));
    }
}

// Get Capacity Table Grid Data With Pagination Saga
function* getCapacityTableGridDataWithPaginationSaga(action) {
    try {
        const response = yield call(services.getCapacityTableGridDataWithPaginationService, action.payload);

        if (response && response.success) {
            yield put(actions.getCapacityTableGridDataWithPaginationSuccess(response));
        } else {
            yield put(actions.getCapacityTableGridDataWithPaginationFailure(response?.errorMessage || 'Failed to fetch capacity table grid data'));
        }
    } catch (error) {
        yield put(actions.getCapacityTableGridDataWithPaginationFailure(error.message || 'An error occurred'));
    }
}

// Draft Capacity Table Report Saga
function* draftCapacityTableReportSaga(action) {
    try {
        const response = yield call(services.draftCapacityTableReportService, action.payload);

        if (response && response.success) {
            yield put(actions.draftCapacityTableReportSuccess(response));

            // Show success message
            SweetAlert.success({
                title: 'Success!',
                text: response.resultMessage || 'Capacity Table Report drafted successfully.',
                showConfirmButton: true
            });
        } else {
            yield put(actions.draftCapacityTableReportFailure(response?.errorMessage || 'Failed to draft capacity table report'));

            // Show error message
            SweetAlert.error({
                title: 'Error!',
                text: response?.errorMessage || 'Failed to draft capacity table report',
                showConfirmButton: true
            });
        }
    } catch (error) {
        yield put(actions.draftCapacityTableReportFailure(error.message || 'An error occurred'));

        // Show error message
        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'An error occurred while drafting capacity table report',
            showConfirmButton: true
        });
    }
}

// Shift Paste Input Duplicate Check Saga
function* shiftPasteInputDuplicateCheckSaga(action) {
    try {
        const response = yield call(services.shiftPasteInputDuplicateCheckService, action.payload);

        if (response && response.success !== undefined) {
            yield put(actions.shiftPasteInputDuplicateCheckSuccess(response));
            return response;
        } else {
            yield put(actions.shiftPasteInputDuplicateCheckFailure(response?.errorMessage || 'Failed to check for duplicate shift paste inputs'));
            return response;
        }
    } catch (error) {
        yield put(actions.shiftPasteInputDuplicateCheckFailure(error.message || 'An error occurred'));
        // Don't throw error to prevent saga cancellation
        return { success: false, error: error.message };
    }
}

// Get Capacity Table Refresh Data Saga
function* getCapacityTableRefreshDataSaga(action) {
    try {
        const response = yield call(services.getCapacityTableRefreshDataService, action.payload);

        if (response && response.success) {
            yield put(actions.getCapacityTableRefreshDataSuccess(response));

            // Show success message
            SweetAlert.success({
                title: 'Refreshed!',
                text: 'Your report has been refreshed.',
                confirmButtonColor: '#17a2b8',
                customClass: {
                    popup: 'swal2-popup-custom',
                    title: 'swal2-title-custom',
                    content: 'swal2-content-custom'
                }
            });
        }
    } catch (error) {
        yield put(actions.getCapacityTableRefreshDataFailure(error.message || 'An error occurred'));

        // Show error message
        SweetAlert.error({
            title: 'Error!',
            text: error.message || 'An error occurred while refreshing capacity table data',
            confirmButtonColor: '#dc3545',
            customClass: {
                popup: 'swal2-popup-custom',
                title: 'swal2-title-custom',
                content: 'swal2-content-custom'
            }
        });
    }
}
// Get Simulator RPCE With Pagination Saga
function* getSimulatorWorklistWithPaginationSaga(action) {
    try {
        const response = yield call(services.getSimulatorWorklistWithPaginationService, action.payload);

        if (response && response.success) {
            yield put(actions.getSimulatorWorklistWithPaginationSuccess(response));
        } else {
            yield put(actions.getSimulatorWorklistWithPaginationFailure(response?.errorMessage || 'Failed to fetch simulator RPCE data'));
        }
    } catch (error) {
        yield put(actions.getSimulatorWorklistWithPaginationFailure(error.message || 'An error occurred'));
    }
}

// Get Capacity RPCE Details Saga
function* getCapacityRPCEDetailsSaga(action) {
    try {
        const response = yield call(services.getCapacityRPCEDetailsService, action.payload);

        if (response && response.success) {
            yield put(actions.getCapacityRPCEDetailsSuccess(response));
        } else {
            yield put(actions.getCapacityRPCEDetailsFailure(response?.errorMessage || 'Failed to fetch capacity RPCE details'));
        }
    } catch (error) {
        yield put(actions.getCapacityRPCEDetailsFailure(error.message || 'An error occurred'));
    }
}

// Get Capacity Details Saga (for worklist)
function* getCapacityDetailsSaga(action) {
    try {
        const response = yield call(services.getCapacityDetailsService, action.payload);

        if (response && response.success) {
            yield put(actions.getCapacityDetailsSuccess(response));
        } else {
            yield put(actions.getCapacityDetailsFailure(response?.errorMessage || 'Failed to fetch capacity details'));
        }
    } catch (error) {
        yield put(actions.getCapacityDetailsFailure(error.message || 'An error occurred'));
    }
}

// Get All Emulator Data Saga (for simulator refresh - worklist)
function* getAllEmulatorDataSaga(action) {
    try {
        const response = yield call(services.getAllEmulatorDataService, action.payload);

        if (response && response.success) {
            yield put(actions.getAllEmulatorDataSuccess(response));
            
            // After first API success, call the second API with same payload
            yield put(actions.getAllEmulatorRPCEDataRequest(action.payload));
        } else {
            yield put(actions.getAllEmulatorDataFailure(response?.errorMessage || 'Failed to refresh simulator data'));
        }
    } catch (error) {
        yield put(actions.getAllEmulatorDataFailure(error.message || 'An error occurred'));
    }
}

// Get All Emulator RPCE Data Saga (for simulator refresh - RPCE)
function* getAllEmulatorRPCEDataSaga(action) {
    try {
        const response = yield call(services.getAllEmulatorRPCEDataService, action.payload);

        if (response && response.success) {
            yield put(actions.getAllEmulatorRPCEDataSuccess(response));
            
            // After refreshing RPCE emulator data, refresh simulator RPCE table data
            const simulatorPayload = {
                CapacityTableID: action.payload?.CapacityTableId || action.payload?.CapacityTableID || 0,
                SkipNoOfRecords: 0,
                PageSize: 1000,
                sSearch: '',
                sortColumn: 'CapacityDate',
                sortColumnDir: 'asc',
                ShowOnlyDifferenceData: false
            };

            // Clear new-schedules-added UI flag when refresh completes
            yield put(actions.updateCapacityTableReducer({ simulatorNewSchedulesAdded: false }));

            yield put(actions.getSimulatorRPCEWithPaginationRequest(simulatorPayload));

            // Show success message after both APIs complete
            SweetAlert.success({
                title: 'Success!',
                text: 'Simulator data refreshed successfully.',
                timer: 2000,
                showConfirmButton: true
            });
        } else {
            yield put(actions.getAllEmulatorRPCEDataFailure(response?.errorMessage || 'Failed to refresh RPCE simulator data'));
        }
    } catch (error) {
        yield put(actions.getAllEmulatorRPCEDataFailure(error.message || 'An error occurred'));
    }
}

// Get QGenda Tasks And Schedules Saga
function* getQgendaTasksAndSchedulesSaga(action) {
    try {
        const response = yield call(services.getQgendaTasksAndSchedulesService, action.payload);

        if (response && response.success) {
            yield put(actions.getQgendaTasksAndSchedulesSuccess(response));
            // After successful QGenda import, refresh the Shift Paste Input grid
            const state = yield select(state => state.capacityTableReducer);
            const capacityTableId = state.capacityTableReportData?.CapacityTableID || state.selectedCapacityTable?.CapacityTableID || 0;

            const paginationPayload = {
                CapacityTableID: capacityTableId,
                SkipNoOfRecords: 0,
                PageSize: 100,
                sSearch: '',
                sortColumn: 'IsExcluded',
                sortColumnDir: 'desc',
                IsQgendaSchedule:false,
            };
            yield put(actions.getShiftPasteInputRefreshDataWithPaginationRequest(paginationPayload));
        } else {
            yield put(actions.getQgendaTasksAndSchedulesFailure(response?.errorMessage || 'Failed to fetch QGenda tasks and schedules'));
        }
    } catch (error) {
        yield put(actions.getQgendaTasksAndSchedulesFailure(error.message || 'An error occurred'));
    }
}

// Root Saga
export default function* capacityTableSaga() {
    yield takeLatest(types.FETCH_CAPACITY_TABLE_REPORTS_REQUEST, fetchCapacityTableReportsSaga);
    yield takeLatest(types.FETCH_CAPACITY_NOTES_EDIT_REQUEST, fetchCapacityNotesEditSaga);
    yield takeLatest(types.FETCH_CAPACITY_NOTES_SAVE_REQUEST, fetchCapacityNotesSaveSaga);
    yield takeLatest(types.DELETE_CAPACITY_REPORT_REQUEST, deleteCapacityReportSaga);
    yield takeLatest(types.GET_ALL_CAPACITY_TABLE_PUBLISHED_REPORT_LIST_REQUEST, getAllCapacityTablePublishedReportListSaga);
    yield takeLatest(types.ENABLE_DISABLE_CAPACITY_TABLE_REPORT_REQUEST, enableDisableCapacityTableReportSaga);
    yield takeLatest(types.COVERAGE_GAP_TOOL_DATA_REFRESH_REQUEST, coverageGapToolDataRefreshSaga);
    yield takeLatest(types.GET_RAD_SHIFT_PASTE_INPUT_ALL_DD_LIST_REQUEST, getRadShiftPasteInputAllDDListSaga);
    yield takeLatest(types.GET_SHIFT_PASTE_INPUT_FILTER_DATA_WITH_PAGINATION_REQUEST, getShiftPasteInputFilterDataWithPaginationSaga);
    yield takeLatest(types.GET_SHIFT_PASTE_INPUT_REFRESH_DATA_WITH_PAGINATION_REQUEST, getShiftPasteInputRefreshDataWithPaginationSaga);
    yield takeLatest(types.PUBLISH_CAPACITY_TABLE_REPORT_REQUEST, publishCapacityTableReportSaga);
    yield takeLatest(types.GET_CAPACITY_TABLE_REPORT_REQUEST, getCapacityTableReportSaga);
    yield takeLatest(types.GET_ACTIVE_RAD_DATA_BY_ID_REQUEST, getActiveRadDataByIdSaga);
    yield takeLatest(types.GET_PENDING_RAD_DATA_BY_ID_REQUEST, getPendingRadDataByIdSaga);
    yield takeLatest(types.GET_OFFBOARDING_RAD_DATA_BY_ID_REQUEST, getOffboardingRadDataByIdSaga);
    yield takeLatest(types.SAVE_IS_EXCLUDED_REQUEST, saveIsExcludedSaga);
    yield takeLatest(types.GET_DUPLICATE_SCHEDULE_DATA_REQUEST, getDuplicateScheduleDataSaga);
    yield takeLatest(types.SAVE_RADIOLOGIST_SHIFT_INPUT_DETAILS_REQUEST, saveRadiologistShiftInputDetailsSaga);
    yield takeLatest(types.DELETE_SHIFT_INPUT_DETAILS_REQUEST, deleteShiftInputDetailsSaga);
    yield takeLatest(types.GET_SHIFT_INPUT_DETAILS_BY_ID_REQUEST, getShiftInputDetailsByIdSaga);
    yield takeLatest(types.GET_SIMULATOR_RPCE_WITH_PAGINATION_REQUEST, getSimulatorRPCEWithPaginationSaga);
    yield takeLatest(types.GET_CAPACITY_TABLE_GRID_DATA_WITH_PAGINATION_REQUEST, getCapacityTableGridDataWithPaginationSaga);
    yield takeLatest(types.UPDATE_RADIOLOGIST_SHIFT_INPUT_DETAILS_REQUEST, updateRadiologistShiftInputDetailsSaga);
    yield takeLatest(types.DRAFT_CAPACITY_TABLE_REPORT_REQUEST, draftCapacityTableReportSaga);
    yield takeLatest(types.SHIFT_PASTE_INPUT_DUPLICATE_CHECK_REQUEST, shiftPasteInputDuplicateCheckSaga);
    yield takeLatest(types.GET_CAPACITY_TABLE_REFRESH_DATA_REQUEST, getCapacityTableRefreshDataSaga);
    yield takeLatest(types.GET_SIMULATOR_WORKLIST_WITH_PAGINATION_REQUEST, getSimulatorWorklistWithPaginationSaga);
    yield takeLatest(types.GET_CAPACITY_RPCE_DETAILS_REQUEST, getCapacityRPCEDetailsSaga);
    yield takeLatest(types.GET_CAPACITY_DETAILS_REQUEST, getCapacityDetailsSaga);
    yield takeLatest(types.GET_ALL_EMULATOR_DATA_REQUEST, getAllEmulatorDataSaga);
    yield takeLatest(types.GET_ALL_EMULATOR_RPCE_DATA_REQUEST, getAllEmulatorRPCEDataSaga);
    yield takeLatest(types.GET_QGENDA_TASKS_SCHEDULES_REQUEST, getQgendaTasksAndSchedulesSaga);
    yield takeLatest(types.DELETE_DUPLICATE_SHIFT_PASTE_INPUT_DATA_REQUEST, deleteDuplicateShiftPasteInputDataSaga);
}
